import { Node, mergeAttributes } from '@tiptap/core';
import type { MarkdownNodeSpec } from 'tiptap-markdown';
import container from 'markdown-it-container';

/**
 * The whitelabels callout block — `::: tip|warning|note|key … :::` in the
 * markdown dialect (`docs/blog-authoring-plan.md`, rendering contract). In the
 * editor it is an `<aside data-callout="kind">` wrapping block content; the
 * Markdown extension serializes it back to the `:::` fence and parses the fence
 * via markdown-it-container with a matching custom render.
 */

export const CALLOUT_KINDS = ['tip', 'warning', 'note', 'key'] as const;
export type CalloutKind = (typeof CALLOUT_KINDS)[number];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      /** Wrap in / unwrap from a callout; retint in place when the kind differs. */
      toggleCallout: (kind: CalloutKind) => ReturnType;
    };
  }
}

// Minimal structural type for markdown-it-container's render tokens; the full
// markdown-it Token type would drag in a types package for two fields.
type ContainerToken = { nesting: number };

// tiptap-markdown bundles its own (older) @types/markdown-it, which clashes
// with the root one @types/markdown-it-container references — the two `use()`
// signatures are structurally incompatible. `md.use(plugin, ...args)` is by
// contract just `plugin(md, ...args)`, so apply the plugin directly through a
// minimal structural signature instead of fighting the duplicated types.
const applyContainer = container as unknown as (
  md: unknown,
  name: string,
  opts: { render: (tokens: ContainerToken[], idx: number) => string },
) => void;

export const Callout = Node.create({
  name: 'callout',

  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      kind: {
        default: 'note',
        parseHTML: (element: HTMLElement): string => element.getAttribute('data-callout') ?? 'note',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'aside[data-callout]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['aside', mergeAttributes(HTMLAttributes, { 'data-callout': String(node.attrs.kind) }), 0];
  },

  addCommands() {
    return {
      toggleCallout:
        (kind: CalloutKind) =>
        ({ editor, commands }) => {
          if (editor.isActive(this.name, { kind })) return commands.lift(this.name);
          if (editor.isActive(this.name)) return commands.updateAttributes(this.name, { kind });
          return commands.wrapIn(this.name, { kind });
        },
    };
  },

  addStorage() {
    const markdown: MarkdownNodeSpec = {
      serialize(state, node) {
        state.write(`::: ${String(node.attrs.kind)}\n`);
        state.renderContent(node);
        state.write(':::');
        state.closeBlock(node);
      },
      parse: {
        setup(markdownit) {
          CALLOUT_KINDS.forEach((kind) => {
            applyContainer(markdownit, kind, {
              render: (tokens: ContainerToken[], idx: number): string =>
                tokens[idx].nesting === 1 ? `<aside data-callout="${kind}">` : '</aside>',
            });
          });
        },
      },
    };
    return { markdown };
  },
});

export default Callout;
